"use client"

import { useRef } from "react"
import { ImportSubscribersConfirmationModal } from "./ImportSubscribersConfirmationModal"
import { ImportFailedModal } from "./ImportFailedModal"
import { ImportCompleteModal } from "./ImportCompleteModal/index"
import { CommunityEntryRes } from "@types"
import { SettingsHeader } from "../../../components/SettingsHeader"
import { ImportSubscribersUpload } from "./ImportSubscribersUpload"
import { ImportSubscribersDestination } from "./ImportSubscribersDestination"
import { ImportSubscribersButton } from "./ImportSubscribersButton"
import { ImportSubscribersProgress } from "./ImportSubscribersProgress"
import { useImportSubscribersLogic } from "../hooks/useImportSubscribersLogic"

export default function ImportSubscribers({
  segments,
}: {
  segments: CommunityEntryRes[]
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    selectedFile,
    importInProgress,
    importProgress,
    selectedSegment,
    showConfirmationModal,
    setShowConfirmationModal,
    showFailedModal,
    setShowFailedModal,
    showCompleteModal,
    setShowCompleteModal,
    importErrors,
    importJobDetails,
    handleFileChange,
    handleImportClick,
    handleConfirmedImport,
    handleSegmentSelect,
    handleTryAgain,
  } = useImportSubscribersLogic()

  // Type cast to any to avoid TS errors since the API might return additional properties
  // that are not explicitly defined in the Job or JobEmailImport types
  const jobDetails = importJobDetails as any

  // Extract stats from job details if available
  const totalEmails = jobDetails?.details?.totalRows ?? 0
  const successfulImports =
    jobDetails?.details?.successfulImports ?? jobDetails?.countSuccess ?? 0
  const skippedEmails = jobDetails?.details?.skippedEmails ?? 0
  const failedEmails =
    jobDetails?.details?.failedEmails ?? jobDetails?.countFailure ?? 0

  return (
    <>
      <section className="space-y-6">
        <SettingsHeader
          title="Import subscribers"
          description={
            <>
              Bring your email list to Paragraph by uploading a CSV file. The
              file must include an email column, and you can optionally include
              created_at for more details.{" "}
              <a
                href="/example_email_list.csv"
                className="text-primary underline"
              >
                Here's an example CSV
              </a>
              . Large email lists may take some time to process.
            </>
          }
        />

        <ImportSubscribersUpload
          fileInputRef={fileInputRef}
          handleFileChange={handleFileChange}
          importInProgress={importInProgress}
        />

        {segments.length > 0 && (
          <ImportSubscribersDestination
            communities={segments}
            selectedSegment={selectedSegment}
            importInProgress={importInProgress}
            onSegmentSelect={handleSegmentSelect}
            selectedSegmentId={selectedSegment?.id}
          />
        )}

        <ImportSubscribersButton
          selectedFile={selectedFile}
          importInProgress={importInProgress}
          onClick={handleImportClick}
        />

        {/* Show progress inline when import is in progress or completed */}
        {(importInProgress || importJobDetails) && (
          <div className="mt-6 p-4 border rounded-md bg-muted/30">
            <ImportSubscribersProgress
              progress={importInProgress ? importProgress : 100}
              totalEmails={totalEmails}
              successfulImports={successfulImports}
              skippedEmails={skippedEmails}
              failedEmails={failedEmails}
              segmentName={selectedSegment?.name}
              importCompleted={!importInProgress && !!importJobDetails}
            />
          </div>
        )}
      </section>

      {/* Modals */}
      <ImportSubscribersConfirmationModal
        open={showConfirmationModal}
        onOpenChange={setShowConfirmationModal}
        segmentName={selectedSegment?.name}
        onConfirm={handleConfirmedImport}
      />

      <ImportFailedModal
        open={showFailedModal}
        onOpenChange={setShowFailedModal}
        errors={importErrors}
        onTryAgain={() => handleTryAgain(fileInputRef)}
      />

      <ImportCompleteModal
        open={showCompleteModal}
        onOpenChange={setShowCompleteModal}
        job={importJobDetails}
        type="subscribers"
      />
    </>
  )
}
